import React, { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import lottie from 'lottie-web/build/player/lottie_light'

import {
  setIsPaymentStatusShown,
  setPaymentAnimationStatus,
  setSubscriptionListAction,
} from 'root-redux/actions/common'
import { getUserStatusAction } from 'root-redux/actions/user'
import {
  selectIsPaymentStatusShown,
  selectPaymentAnimationStatus,
} from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { useLockScroll } from 'hooks/useLockScroll'

import { AnimationState } from 'modules/purchase/constants'

import paymentErrorAnimation from 'assets/animation/paymentErrorAnimation.json'
import paymentSuccessAnimation from 'assets/animation/paymentSuccessAnimation.json'

import { StyledPaymentModal as S } from './PaymentStatusModal.styles'

const commonLottieProps = {
  width: '132px',
  height: '132px',
}

export const PaymentStatusModal: React.FC = () => {
  const { t } = useTranslation()

  const animationStatus = useSelector(selectPaymentAnimationStatus)
  const dispatch: TAppDispatch = useDispatch()
  const uuid = useSelector(selectUUID)
  const isPaymentStatusShown = useSelector(selectIsPaymentStatusShown)

  const completeAnimationRef = useRef<HTMLDivElement | null>(null)
  const errorAnimationRef = useRef<HTMLDivElement | null>(null)

  useLockScroll(isPaymentStatusShown)

  useEffect(() => {
    switch (animationStatus) {
      case AnimationState.SUCCESS:
        if (completeAnimationRef.current) {
          lottie.loadAnimation({
            container: completeAnimationRef.current,
            animationData: paymentSuccessAnimation,
            loop: false,
          })
        }
        break
      case AnimationState.ERROR:
        if (errorAnimationRef.current) {
          lottie.loadAnimation({
            container: errorAnimationRef.current,
            animationData: paymentErrorAnimation,
            loop: false,
          })
        }
        break
      default:
        break
    }

    return () => lottie.destroy()
  }, [animationStatus])

  const handleSuccessButtonClick = useCallback(() => {
    dispatch(setIsPaymentStatusShown(false))
    dispatch(setSubscriptionListAction([]))
    dispatch(getUserStatusAction(uuid))
  }, [dispatch, uuid])

  const handleResetError = useCallback(() => {
    dispatch(setIsPaymentStatusShown(false))
    dispatch(setPaymentAnimationStatus(false))
  }, [dispatch])

  return isPaymentStatusShown ? (
    <S.ModalWrapper>
      <S.Container>
        <S.LottieContainer>
          {animationStatus === AnimationState.SUCCESS && (
            <>
              <div
                ref={completeAnimationRef}
                style={{ ...commonLottieProps }}
              />
              <S.PaymentStatus>
                {t('purchase1.paymentWaiting.paymentWasSuccessful')}
              </S.PaymentStatus>

              <S.SuccessButton type="button" onClick={handleSuccessButtonClick}>
                {t('purchase1.paymentWaiting.proceedToPlan')}
              </S.SuccessButton>

              <S.Disclaimer>
                {t('purchase1.paymentWaiting.disclaimerForSuccessfulPayment')}
              </S.Disclaimer>
            </>
          )}
          {animationStatus === AnimationState.ERROR && (
            <>
              <div ref={errorAnimationRef} style={{ ...commonLottieProps }} />

              <S.ErrorStatus>
                {t('purchase1.paymentWaiting.errorDuringPayment')}
              </S.ErrorStatus>
              <S.ErrorInfo />

              <S.ErrorButton onClick={handleResetError} type="button">
                {t`actions.tryAgain`}
              </S.ErrorButton>
            </>
          )}
        </S.LottieContainer>
      </S.Container>
    </S.ModalWrapper>
  ) : null
}
